import type { Side } from '../math/geometry';

interface Props {
  readonly angleLabel: string;
  readonly picked: Side | null;
  readonly answer: Side;
  readonly revealed: boolean;
  readonly onPick: (side: Side) => void;
}

const SPOTS: ReadonlyArray<{ id: Side; label: string; style: React.CSSProperties }> = [
  { id: 'adj', label: 'A', style: { left: 112, bottom: 4 } },
  { id: 'opp', label: 'B', style: { right: 2, bottom: 82 } },
  { id: 'hyp', label: 'C', style: { left: 122, bottom: 100 } },
];

/**
 * A right triangle with the marked angle at the lower left.
 *
 * The letters are neutral on purpose. Labelling the sides "opposite" and
 * "adjacent" on the drawing would answer the question the drill is asking.
 */
export function Triangle({ angleLabel, picked, answer, revealed, onPick }: Props) {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: '6px 0' }}>
      <div style={{ position: 'relative', width: 300, height: 196 }}>
        {/* adjacent — the base */}
        <div
          style={{
            position: 'absolute',
            left: 24,
            bottom: 26,
            width: 246,
            height: 3,
            background: 'rgba(255,255,255,.3)',
          }}
        />
        {/* opposite — the vertical leg */}
        <div
          style={{
            position: 'absolute',
            right: 30,
            bottom: 26,
            width: 3,
            height: 126,
            background: 'rgba(255,255,255,.3)',
          }}
        />
        {/* hypotenuse */}
        <div
          style={{
            position: 'absolute',
            left: 24,
            bottom: 26,
            width: 277,
            height: 3,
            background: 'rgba(255,255,255,.3)',
            transformOrigin: 'left center',
            transform: 'rotate(-27.1deg)',
          }}
        />
        {/* square-corner mark */}
        <div
          style={{
            position: 'absolute',
            right: 33,
            bottom: 29,
            width: 16,
            height: 16,
            borderLeft: '2px solid rgba(255,255,255,.22)',
            borderTop: '2px solid rgba(255,255,255,.22)',
          }}
        />
        <div
          style={{
            position: 'absolute',
            left: 46,
            bottom: 34,
            font: '600 13px/1 var(--font-display)',
            color: 'var(--amber)',
          }}
        >
          {angleLabel}
        </div>

        {SPOTS.map((spot) => {
          const isPicked = picked === spot.id;
          const isAnswer = revealed && spot.id === answer;
          const isWrong = revealed && isPicked && spot.id !== answer;
          const colour = isAnswer
            ? 'var(--lime)'
            : isWrong
              ? 'var(--red)'
              : isPicked
                ? 'var(--lime)'
                : 'var(--text-4)';

          return (
            <button
              key={spot.id}
              type="button"
              onClick={() => onPick(spot.id)}
              disabled={revealed}
              aria-label={`Side ${spot.label}`}
              aria-pressed={isPicked}
              style={{
                position: 'absolute',
                ...spot.style,
                width: 44,
                height: 44,
                borderRadius: 999,
                border: `2px solid ${colour}`,
                background: isPicked || isAnswer ? 'oklch(0.82 0.17 130 / .18)' : 'var(--surface-2)',
                color: colour,
                font: '700 14px/1 var(--font-display)',
                cursor: revealed ? 'default' : 'pointer',
                transition: 'border-color var(--motion), background var(--motion)',
              }}
            >
              {spot.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
